import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
  HStack,
  Image,
  Box,
  Spinner,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  InputGroup,
  InputLeftElement,
  Icon,
  Divider,
} from "@chakra-ui/react";
import { useContext, useState, useRef, useEffect } from "react";
import { DialogProvider, createDialogContext } from "../../../Common/Dialog";
import { AddIcon } from "@chakra-ui/icons";
import ReactS3Uploader, { S3Response } from "react-s3-uploader";
import actions from "../../../actions";
import {
  getIconComponent,
  getIconForSocial,
} from "../../FeedManager/SocialInput";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

export const AddManualArtistDialogContext = createDialogContext();

// Validation schema
const schema = yup.object({
  name: yup.string().required("Artist name is required"),
  image_url: yup.string().required("Artist image is required"),
  profiles: yup
    .array()
    .of(
      yup.object({
        platform: yup.string().required(),
        url: yup.string().required(),
      })
    )
    .default([]),
});

type FormData = yup.InferType<typeof schema>;

const AddManualArtistDialog = ({
  isLoading = false,
  onSubmit,
  initialName,
}: {
  isLoading: boolean;
  initialName?: string;
  onSubmit?: (artistData: FormData) => void;
}) => {
  const { isOpen, close } = useContext(AddManualArtistDialogContext);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      name: initialName || "",
      image_url: "",
      profiles: [],
    },
  });

  const [uploadLoading, setUploadLoading] = useState(false);
  const uploadRef = useRef<HTMLInputElement>(null);

  const watchedValues = watch();

  const handleImageUpload = (
    result: S3Response & { cdn_url: string },
    file: File
  ) => {
    setUploadLoading(false);
    setValue("image_url", result.cdn_url, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleFormSubmit = (data: FormData) => {
    if (onSubmit) {
      onSubmit(data);
    }
  };

  const handleClose = () => {
    reset({
      name: "",
      image_url: "",
      profiles: [],
    });
    close();
  };

  const updateProfile = (platform: string, url: string) => {
    const currentProfiles = watchedValues.profiles || [];
    const existingIndex = currentProfiles.findIndex(
      (p) => p.platform === platform
    );

    // If URL is empty or not provided, remove the profile entry
    if (!url || url.trim() === "") {
      if (existingIndex >= 0) {
        const newProfiles = currentProfiles.filter(
          (_, index) => index !== existingIndex
        );
        setValue("profiles", newProfiles, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      return;
    }

    const newProfiles = [...currentProfiles];

    if (existingIndex >= 0) {
      newProfiles[existingIndex] = { platform, url };
    } else {
      newProfiles.push({ platform, url });
    }

    setValue("profiles", newProfiles, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const getProfileUrl = (platform: string) => {
    const profiles = watchedValues.profiles || [];
    const profile = profiles.find((p) => p.platform === platform);
    return profile?.url || "";
  };

  useEffect(() => {
    if (!isOpen) {
      reset({
        name: "",
        image_url: "",
        profiles: [],
      });
      setUploadLoading(false);
    }
  }, [isOpen, initialName, setValue]);

  return (
    <>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={handleClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          mb={[0, "auto"]}
          borderBottomLeftRadius={[0, "md"]}
          borderBottomRightRadius={[0, "md"]}
          maxW="500px"
        >
          <ModalHeader paddingTop={4}>
            <VStack gap={0} width={"full"}>
              <Text w="full" fontSize="20px" fontWeight="600">
                Manually add artist
              </Text>
            </VStack>
          </ModalHeader>
          <ModalBody textAlign={"left"}>
            <VStack w="full" spacing={4}>
              {/* Image Upload Section */}
              <VStack w="full" spacing={3}>
                <Box
                  w="200px"
                  h="200px"
                  borderRadius="8px"
                  overflow="hidden"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="#F7FAFC"
                  position="relative"
                  cursor="pointer"
                  onClick={() => uploadRef.current?.click()}
                >
                  {uploadLoading ? (
                    <Spinner size="lg" color="blue.500" />
                  ) : watchedValues.image_url ? (
                    <VStack w="full">
                      <Image
                        src={watchedValues.image_url}
                        alt="Artist preview"
                        w="full"
                        h="60%"
                        maxW="70%"
                        rounded="xl"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.300"
                        mx={4}
                        aspectRatio={1}
                        objectFit="cover"
                      />
                      <HStack
                        spacing={2}
                        rounded="full"
                        border="1px solid"
                        borderColor="gray.300"
                        p={2}
                        px={4}
                      >
                        <AddIcon color="gray.400" boxSize={2.5} />
                        <Text fontSize="xs" textAlign="center" fontWeight="500">
                          Change image
                        </Text>
                      </HStack>
                    </VStack>
                  ) : (
                    <HStack
                      spacing={2}
                      rounded="full"
                      border="1px solid"
                      borderColor="gray.300"
                      p={2}
                      px={4}
                    >
                      <AddIcon color="gray.400" boxSize={2.5} />
                      <Text fontSize="xs" textAlign="center" fontWeight="500">
                        Upload artist image
                      </Text>
                    </HStack>
                  )}
                </Box>
                {errors.image_url && (
                  <Text fontSize="sm" color="red.500">
                    {errors.image_url.message}
                  </Text>
                )}
              </VStack>

              {/* Artist Name Field */}
              <FormControl isInvalid={!!errors.name}>
                <FormLabel
                  fontSize="16px"
                  fontWeight="600"
                  color="gray.700"
                  alignSelf="flex-start"
                >
                  Artist name (required)
                </FormLabel>
                <Input
                  {...register("name")}
                  placeholder="Enter artist name"
                  size="lg"
                  borderRadius="8px"
                  border="1px solid #E2E8F0"
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px #3182CE",
                  }}
                />
                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              </FormControl>
              <Divider borderColor="gray.300" />

              {/* Social Links Section */}
              <VStack
                w="full"
                spacing={2}
                rounded="xl"
                background="white"
                border="1px solid"
                borderColor="gray.300"
                p={4}
              >
                <Text
                  fontSize="16px"
                  fontWeight="600"
                  color="gray.700"
                  alignSelf="flex-start"
                >
                  Social Links
                </Text>

                {/* Instagram */}
                <HStack w="full">
                  <InputGroup w="full">
                    <InputLeftElement
                      alignItems="center"
                      w={12}
                      h={12}
                      justifyContent="center"
                      display="flex"
                    >
                      <Icon
                        fontSize="18px"
                        as={getIconComponent("instagram")}
                      />
                    </InputLeftElement>
                    <Input
                      value={getProfileUrl("instagram")}
                      onChange={(e) =>
                        updateProfile("instagram", e.target.value)
                      }
                      placeholder="https://instagram.com/username"
                      size="lg"
                      fontSize="sm"
                      borderRadius="8px"
                      border="1px solid #E2E8F0"
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182CE",
                      }}
                    />
                  </InputGroup>
                </HStack>

                {/* Spotify */}
                <HStack w="full">
                  <InputGroup w="full">
                    <InputLeftElement
                      alignItems="center"
                      w={12}
                      h={12}
                      justifyContent="center"
                      display="flex"
                    >
                      <Icon fontSize="18px" as={getIconComponent("spotify")} />
                    </InputLeftElement>
                    <Input
                      value={getProfileUrl("spotify")}
                      onChange={(e) => updateProfile("spotify", e.target.value)}
                      placeholder="https://open.spotify.com/artist/..."
                      size="lg"
                      fontSize="sm"
                      borderRadius="8px"
                      border="1px solid #E2E8F0"
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182CE",
                      }}
                    />
                  </InputGroup>
                </HStack>

                {/* TikTok */}
                <HStack w="full">
                  <InputGroup w="full">
                    <InputLeftElement
                      alignItems="center"
                      w={12}
                      h={12}
                      justifyContent="center"
                      display="flex"
                    >
                      <Icon fontSize="18px" as={getIconComponent("tiktok")} />
                    </InputLeftElement>
                    <Input
                      value={getProfileUrl("tiktok")}
                      onChange={(e) => updateProfile("tiktok", e.target.value)}
                      placeholder="https://www.tiktok.com/@username"
                      size="lg"
                      fontSize="sm"
                      borderRadius="8px"
                      border="1px solid #E2E8F0"
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182CE",
                      }}
                    />
                  </InputGroup>
                </HStack>

                {/* YouTube */}
                <HStack w="full">
                  <InputGroup w="full">
                    <InputLeftElement
                      alignItems="center"
                      w={12}
                      h={12}
                      justifyContent="center"
                      display="flex"
                    >
                      <Icon fontSize="18px" as={getIconComponent("youtube")} />
                    </InputLeftElement>
                    <Input
                      value={getProfileUrl("youtube")}
                      onChange={(e) => updateProfile("youtube", e.target.value)}
                      placeholder="https://www.youtube.com/@channel"
                      size="lg"
                      fontSize="sm"
                      borderRadius="8px"
                      border="1px solid #E2E8F0"
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182CE",
                      }}
                    />
                  </InputGroup>
                </HStack>

                {/* Website */}
                <HStack w="full">
                  <InputGroup w="full">
                    <InputLeftElement
                      alignItems="center"
                      w={12}
                      h={12}
                      justifyContent="center"
                      display="flex"
                    >
                      <Icon fontSize="18px" as={getIconComponent("website")} />
                    </InputLeftElement>
                    <Input
                      value={getProfileUrl("website")}
                      onChange={(e) => updateProfile("website", e.target.value)}
                      placeholder="https://www.website.com"
                      size="lg"
                      fontSize="sm"
                      borderRadius="8px"
                      border="1px solid #E2E8F0"
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182CE",
                      }}
                    />
                  </InputGroup>
                </HStack>
              </VStack>

              {/* Hidden S3 Uploader */}
              <ReactS3Uploader
                style={{ display: "none" }}
                //@ts-ignore
                inputRef={uploadRef}
                getSignedUrl={(file, callback) =>
                  actions.ADMIN.createPublicUploadUrl(file, callback)
                }
                accept="image/*"
                onProgress={() => {}}
                onError={(e) => {
                  setUploadLoading(false);
                }}
                preprocess={(f, next) => {
                  setUploadLoading(true);
                  // Create temporary URL for preview
                  next(f);
                }}
                onFinish={handleImageUpload}
                uploadRequestHeaders={(file) => ({
                  "Content-Type": file.type,
                  "x-amz-acl": "private",
                })}
                contentDisposition="auto"
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Stack direction={["column", "row-reverse"]} width="100%">
              <Button
                onClick={handleSubmit(handleFormSubmit)}
                variant="solid"
                colorScheme="green"
                borderRadius="8px"
                isLoading={isLoading}
                isDisabled={!isValid}
              >
                Done
              </Button>
              <Button
                onClick={handleClose}
                variant="outline"
                borderRadius="8px"
              >
                Cancel
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const withAddManualArtistDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={AddManualArtistDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default AddManualArtistDialog;
